import { ArrowRight, Globe, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { motion } from "framer-motion";

export const Hero = ({ isLoaded }) => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background Video */}
      <div className="absolute inset-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
        >
          <source 
            src="https://www.m2l-usa.com/wp-content/uploads/2017/11/566ce3988bd642ca1d1761dc0d058785.webm" 
            type="video/webm" 
          />
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-[#060B19]/95 via-[#060B19]/80 to-[#060B19]/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#060B19] via-transparent to-transparent" />
      </div>

      {/* Ambient glow effects */}
      <div className="absolute top-1/4 left-0 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[120px] pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-32">
        <div className="max-w-3xl">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 glass-card rounded-full px-4 py-2 mb-8"
          >
            <Globe className="w-4 h-4 text-primary" />
            <span className="text-white/90 text-sm font-medium">
              Global Education Network
            </span>
          </motion.div>

          {/* Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-heading text-5xl md:text-6xl lg:text-7xl font-black tracking-tight leading-tight text-white mb-8"
            data-testid="hero-heading"
          >
            Building Global Pathways for{" "}
            <span className="gradient-text">Learning & Leadership</span>
          </motion.h1>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed mb-10"
            data-testid="hero-subheading"
          >
            Connecting students, professionals, and institutions across continents. 
            We bridge the United States with the world through higher education, 
            executive training, and professional development.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 mb-16"
          >
            <a href="#contact">
              <Button
                data-testid="hero-cta-primary"
                className="bg-primary text-[#060B19] hover:bg-primary-hover rounded-lg px-8 py-6 text-lg font-semibold transition-all group btn-glow"
              >
                Start Your Journey
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
            <a href="#services">
              <Button
                data-testid="hero-cta-secondary"
                variant="outline"
                className="border-2 border-white/20 text-white hover:bg-white/10 hover:border-primary/50 rounded-lg px-8 py-6 text-lg font-semibold transition-all bg-transparent"
              >
                Explore Programs
              </Button>
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-wrap gap-10 pt-8 border-t border-white/10"
          >
            {[
              { value: "50+", label: "Countries Served" },
              { value: "200+", label: "Partner Institutions" },
              { value: "10K+", label: "Global Alumni" },
              { value: "95%", label: "Success Rate" },
            ].map((stat, idx) => (
              <div key={idx} className="text-left">
                <div className="text-3xl md:text-4xl font-heading font-black text-primary mb-1">
                  {stat.value}
                </div>
                <div className="text-white/50 text-sm">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-white/20 rounded-full flex justify-center pt-2">
          <div className="w-1 h-3 bg-primary rounded-full animate-bounce" />
        </div>
      </motion.div>
    </section>
  );
};
